import type { NostrEvent } from '@nostrify/nostrify';

/** All Better Me community content is tagged with this single-letter tag. */
export const COMMUNITY_TAG = 'calmcycle';

/** Marker tag used on posts published with a throwaway key. */
export const ANON_TAG = 'calmcycle-anon';

export function isAnonymousPost(event: NostrEvent): boolean {
  return event.tags.some(([name, value]) => name === 't' && value === ANON_TAG);
}

/* ------------------------------------------------------------------ */
/* Threading                                                           */
/* ------------------------------------------------------------------ */

export interface Thread {
  root: NostrEvent;
  replies: NostrEvent[];
}

/** Group flat kind-1 events into root questions with flat reply lists. */
export function buildThreads(events: NostrEvent[]): Thread[] {
  const unique = new Map<string, NostrEvent>();
  for (const e of events) {
    if (!unique.has(e.id)) unique.set(e.id, e);
  }

  const roots: NostrEvent[] = [];
  const repliesByRoot = new Map<string, NostrEvent[]>();

  const findRoot = (event: NostrEvent, depth = 0): string | undefined => {
    if (depth > 10) return undefined;
    const parentId = event.tags.find(([n]) => n === 'e')?.[1];
    if (!parentId) return event.id;
    const parent = unique.get(parentId);
    if (!parent) return parentId; // parent not loaded; attach to its id bucket
    return findRoot(parent, depth + 1);
  };

  for (const event of unique.values()) {
    const isReply = event.tags.some(([n]) => n === 'e');
    if (!isReply) {
      roots.push(event);
      continue;
    }
    const rootId = findRoot(event);
    if (!rootId) continue;
    const arr = repliesByRoot.get(rootId) ?? [];
    arr.push(event);
    repliesByRoot.set(rootId, arr);
  }

  return roots
    .map((root) => ({
      root,
      replies: (repliesByRoot.get(root.id) ?? []).sort((a, b) => a.created_at - b.created_at),
    }))
    .sort((a, b) => b.root.created_at - a.root.created_at);
}

/* ------------------------------------------------------------------ */
/* Time formatting                                                     */
/* ------------------------------------------------------------------ */

export function timeAgo(timestampSec: number): string {
  const diffMs = Date.now() - timestampSec * 1000;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestampSec * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/* ------------------------------------------------------------------ */
/* Built-in sample conversations (only shown if relays return nothing) */
/* ------------------------------------------------------------------ */

export interface SamplePost {
  id: string;
  authorName: string;
  content: string;
  postedHoursAgo: number;
}

export interface SampleThread {
  root: SamplePost;
  replies: SamplePost[];
}

export const SAMPLE_THREADS: SampleThread[] = [
  {
    root: {
      id: 'sample-1',
      authorName: 'Maya R.',
      content:
        'Does anyone else track their HRV around their period? Mine drops from ~55ms to ~35ms the day before and it\'s become my most reliable early warning sign. Curious what patterns others see.',
      postedHoursAgo: 30,
    },
    replies: [
      {
        id: 'sample-1a',
        authorName: 'Anonymous',
        content:
          'Same here! My watch flags "strained" readings almost exactly 24-48h before day 1 every cycle. I now move meetings when I see it dip.',
        postedHoursAgo: 26,
      },
      {
        id: 'sample-1b',
        authorName: 'Sofia L.',
        content:
          'My HRV drops too but my resting heart rate goes UP about 8-10 bpm. Between the two signals I can predict my flare days pretty accurately.',
        postedHoursAgo: 22,
      },
    ],
  },
  {
    root: {
      id: 'sample-2',
      authorName: 'Anonymous',
      content:
        'How do you all handle rescheduling work on severe pain days? I feel guilty canceling but pushing through makes everything worse. Looking for scripts or strategies that actually work.',
      postedHoursAgo: 50,
    },
    replies: [
      {
        id: 'sample-2a',
        authorName: 'Jenna K.',
        content:
          'I keep 2 "flex days" blocked on my calendar around my predicted window. If I don\'t need them, bonus focus time. If I do, no guilt — the time was already mine.',
        postedHoursAgo: 44,
      },
      {
        id: 'sample-2b',
        authorName: 'Anonymous',
        content:
          'I told my manager I have a recurring health thing that flares ~2 days a month. Didn\'t over-explain. Having the conversation once was way easier than calling out sick every month.',
        postedHoursAgo: 40,
      },
    ],
  },
  {
    root: {
      id: 'sample-3',
      authorName: 'Jenna K.',
      content:
        'Bringing a symptom report to my new gynecologist next week. What details made YOUR appointments more productive? I never know how much is too much.',
      postedHoursAgo: 74,
    },
    replies: [
      {
        id: 'sample-3a',
        authorName: 'Maya R.',
        content:
          'Numbers help: "pain 7-8/10 for 3 days each cycle" lands better than "bad cramps". A 30-day printout with pain + HRV trends got me taken seriously for the first time.',
        postedHoursAgo: 70,
      },
      {
        id: 'sample-3b',
        authorName: 'Riley T.',
        content:
          'Write down your top 3 questions beforehand. Appointments fly by and I always forgot half of what I wanted to ask until I started doing this.',
        postedHoursAgo: 65,
      },
    ],
  },
  {
    root: {
      id: 'sample-4',
      authorName: 'Riley T.',
      content:
        'Gentle movement on day 1-2: helpful or harmful for you? My physio swears by short walks but some days even that feels impossible.',
      postedHoursAgo: 96,
    },
    replies: [
      {
        id: 'sample-4a',
        authorName: 'Sofia L.',
        content:
          'Both things can be true. I do a 5-minute rule — walk for 5 minutes, and if it\'s awful I stop with zero guilt. About half the time the walk actually helps.',
        postedHoursAgo: 90,
      },
    ],
  },
  {
    root: {
      id: 'sample-5',
      authorName: 'Sofia L.',
      content:
        'Heat pads vs TENS unit for cramps — which one wins for you? Considering investing in a TENS before my next cycle.',
      postedHoursAgo: 120,
    },
    replies: [
      {
        id: 'sample-4b',
        authorName: 'Anonymous',
        content:
          'TENS was a game changer for my back pain days. I still use heat at night but the TENS lets me function during the day.',
        postedHoursAgo: 110,
      },
      {
        id: 'sample-5b',
        authorName: 'Maya R.',
        content: 'Heat for me — cheap, instant, and no learning curve. YMMV!',
        postedHoursAgo: 100,
      },
    ],
  },
];
